import { create } from 'zustand'
import { persist } from 'zustand/middleware'

// Roles disponibles en el sistema
export const ROLES = {
  ADMIN: 'admin',
  OPERADOR: 'operador',         // Operador de hospital - gestiona alertas operativas
  HOSPITAL_USER: 'hospital_user', // Usuario de hospital - carga datos
  VIEWER: 'viewer'
}

// Permisos por rol
export const PERMISSIONS = {
  [ROLES.ADMIN]: {
    canViewAllHospitals: true,
    canUploadData: true,
    canManageUsers: true,
    canGenerateReports: true,
    canManageAlerts: true,
    canCreateAlerts: true,
    canEditConfig: true,
  },
  [ROLES.OPERADOR]: {
    canViewAllHospitals: false,
    canUploadData: false,
    canManageUsers: false,
    canGenerateReports: false,
    canManageAlerts: true,      // Puede gestionar alertas de su hospital
    canCreateAlerts: true,       // Puede crear alertas operativas
    canEditConfig: false,
  },
  [ROLES.HOSPITAL_USER]: {
    canViewAllHospitals: false,
    canUploadData: true,
    canManageUsers: false,
    canGenerateReports: true,
    canManageAlerts: false,
    canCreateAlerts: false,
    canEditConfig: false,
  },
  [ROLES.VIEWER]: {
    canViewAllHospitals: false,
    canUploadData: false,
    canManageUsers: false,
    canGenerateReports: true,
    canManageAlerts: false,
    canCreateAlerts: false,
    canEditConfig: false,
  }
}

// Store de autenticación con roles
export const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,

      login: (user, token) => set({
        user,
        token,
        isAuthenticated: true
      }),

      logout: () => set({
        user: null,
        token: null,
        isAuthenticated: false
      }),

      updateUser: (userData) => set((state) => ({
        user: { ...state.user, ...userData }
      })),

      // Verificar permiso específico
      hasPermission: (permission) => {
        const { user } = get()
        if (!user?.rol) return false
        return PERMISSIONS[user.rol]?.[permission] ?? false
      },

      // Verificar si puede ver un hospital específico
      canAccessHospital: (hospitalId) => {
        const { user } = get()
        if (!user) return false
        if (user.rol === ROLES.ADMIN) return true
        return user.hospital_id === hospitalId
      },

      // Obtener hospital asignado al usuario
      getUserHospital: () => {
        const { user } = get()
        return user?.hospital_id || null
      },

      // Verificar si es admin
      isAdmin: () => {
        const { user } = get()
        return user?.rol === ROLES.ADMIN
      }
    }),
    {
      name: 'auth-storage',
    }
  )
)

// Store de filtros y selección
export const useFilterStore = create((set) => ({
  // Filtros activos
  filters: {
    hospital_id: null,
    prioridad: null,
    estado: null,
    especialidad_id: null,
    search: '',
  },
  
  // Elemento seleccionado
  selectedPatient: null,
  selectedHospital: null,
  selectedCirugia: null,
  
  // Acciones
  setFilter: (key, value) => set((state) => ({
    filters: { ...state.filters, [key]: value }
  })),
  
  resetFilters: () => set({
    filters: {
      hospital_id: null,
      prioridad: null,
      estado: null,
      especialidad_id: null,
      search: '',
    }
  }),
  
  selectPatient: (patient) => set({ selectedPatient: patient }),
  selectHospital: (hospital) => set({ selectedHospital: hospital }),
  selectCirugia: (cirugia) => set({ selectedCirugia: cirugia }),
  
  clearSelection: () => set({
    selectedPatient: null,
    selectedHospital: null,
    selectedCirugia: null,
  }),
}))

// Store de UI
export const useUIStore = create((set) => ({
  sidebarOpen: true,
  modalOpen: null,
  loading: false,
  
  toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),
  openModal: (modalId) => set({ modalOpen: modalId }),
  closeModal: () => set({ modalOpen: null }),
  setLoading: (loading) => set({ loading }),
}))

// Tipos de alerta
export const ALERT_TYPES = {
  PACIENTE_REZAGADO: 'paciente_rezagado',
  OCUPACION_ALTA: 'ocupacion_alta',
  CIRUGIA_DIFERIDA: 'cirugia_diferida',
  CIRUGIA_CANCELADA: 'cirugia_cancelada',
  ERROR_CARGA: 'error_carga',
  CARGA_EXITOSA: 'carga_exitosa',
  PACIENTE_ALTA_PRIORIDAD: 'paciente_alta_prioridad',
  QUIROFANO_MANTENIMIENTO: 'quirofano_mantenimiento',
  SISTEMA: 'sistema'
}

// Configuración de alertas automáticas
export const ALERT_CONFIG = {
  diasEsperaAlerta: 90,        // Días para considerar paciente rezagado
  ocupacionAlta: 85,           // Porcentaje de ocupación alta
  ocupacionCritica: 95,        // Porcentaje de ocupación crítica
}

// Store de alertas y notificaciones
export const useAlertStore = create(
  persist(
    (set, get) => ({
      alerts: [],
      unreadCount: 0,
      alertConfig: ALERT_CONFIG,

      setAlerts: (alerts) => {
        const unread = alerts.filter(a => !a.leida).length
        set({ alerts, unreadCount: unread })
      },

      addAlert: (alert) => set((state) => {
        const newAlert = {
          id: Date.now(),
          fecha: new Date().toISOString(),
          leida: false,
          ...alert
        }
        return {
          alerts: [newAlert, ...state.alerts],
          unreadCount: state.unreadCount + 1
        }
      }),

      markAsRead: (alertId) => set((state) => ({
        alerts: state.alerts.map(a =>
          a.id === alertId ? { ...a, leida: true } : a
        ),
        unreadCount: Math.max(0, state.unreadCount - 1)
      })),

      markAllAsRead: () => set((state) => ({
        alerts: state.alerts.map(a => ({ ...a, leida: true })),
        unreadCount: 0
      })),

      deleteAlert: (alertId) => set((state) => {
        const alert = state.alerts.find(a => a.id === alertId)
        return {
          alerts: state.alerts.filter(a => a.id !== alertId),
          unreadCount: alert && !alert.leida ? state.unreadCount - 1 : state.unreadCount
        }
      }),

      clearAlerts: () => set({ alerts: [], unreadCount: 0 }),

      setUnreadCount: (count) => set({ unreadCount: count }),

      // Generar alertas automáticas basadas en datos
      generateAlertsFromData: (data, type, hospitalId = null) => {
        const { alertConfig, addAlert } = get()
        const newAlerts = []

        if (type === 'pacientes') {
          // Alertas por pacientes rezagados
          data.forEach(paciente => {
            if (paciente.dias_espera >= alertConfig.diasEsperaAlerta) {
              newAlerts.push({
                tipo: ALERT_TYPES.PACIENTE_REZAGADO,
                prioridad: paciente.dias_espera >= 120 ? 'alta' : 'media',
                titulo: 'Paciente con espera prolongada',
                mensaje: `${paciente.nombre} lleva ${paciente.dias_espera} días en espera para ${paciente.procedimiento}`,
                entidad_tipo: 'paciente',
                entidad_id: paciente.id || paciente.nss,
                hospital_id: paciente.hospital_id || hospitalId,
              })
            }
            // Alertas por alta prioridad
            if (paciente.prioridad === 'Alta') {
              newAlerts.push({
                tipo: ALERT_TYPES.PACIENTE_ALTA_PRIORIDAD,
                prioridad: 'alta',
                titulo: 'Paciente de alta prioridad registrado',
                mensaje: `${paciente.nombre} requiere atención prioritaria: ${paciente.procedimiento}`,
                entidad_tipo: 'paciente',
                entidad_id: paciente.id || paciente.nss,
                hospital_id: paciente.hospital_id || hospitalId,
              })
            }
          })
        }

        if (type === 'hospitales') {
          data.forEach(hospital => {
            if (hospital.ocupacion >= alertConfig.ocupacionCritica) {
              newAlerts.push({
                tipo: ALERT_TYPES.OCUPACION_ALTA,
                prioridad: 'alta',
                titulo: 'Ocupación crítica de quirófanos',
                mensaje: `${hospital.nombre} tiene ocupación del ${hospital.ocupacion}%`,
                entidad_tipo: 'hospital',
                entidad_id: hospital.id,
                hospital_id: hospital.id,
              })
            } else if (hospital.ocupacion >= alertConfig.ocupacionAlta) {
              newAlerts.push({
                tipo: ALERT_TYPES.OCUPACION_ALTA,
                prioridad: 'media',
                titulo: 'Ocupación alta de quirófanos',
                mensaje: `${hospital.nombre} tiene ocupación del ${hospital.ocupacion}%`,
                entidad_tipo: 'hospital',
                entidad_id: hospital.id,
                hospital_id: hospital.id,
              })
            }
          })
        }

        if (type === 'cirugias') {
          data.forEach(cirugia => {
            if (cirugia.estado === 'diferida') {
              newAlerts.push({
                tipo: ALERT_TYPES.CIRUGIA_DIFERIDA,
                prioridad: 'media',
                titulo: 'Cirugía diferida',
                mensaje: `Cirugía de ${cirugia.paciente_nombre || 'paciente'} ha sido diferida: ${cirugia.motivo_diferimiento || 'Sin motivo especificado'}`,
                entidad_tipo: 'cirugia',
                entidad_id: cirugia.id,
                hospital_id: cirugia.hospital_id || hospitalId,
              })
            }
            if (cirugia.estado === 'cancelada') {
              newAlerts.push({
                tipo: ALERT_TYPES.CIRUGIA_CANCELADA,
                prioridad: 'alta',
                titulo: 'Cirugía cancelada',
                mensaje: `Cirugía de ${cirugia.paciente_nombre || 'paciente'} ha sido cancelada: ${cirugia.motivo_cancelacion || 'Sin motivo especificado'}`,
                entidad_tipo: 'cirugia',
                entidad_id: cirugia.id,
                hospital_id: cirugia.hospital_id || hospitalId,
              })
            }
          })
        }

        // Agregar todas las alertas generadas
        newAlerts.forEach(alert => addAlert(alert))
        return newAlerts.length
      },

      // Alerta de carga de datos
      addUploadAlert: (success, filename, type, records, error = null, hospitalId = null) => {
        const { addAlert } = get()
        if (success) {
          addAlert({
            tipo: ALERT_TYPES.CARGA_EXITOSA,
            prioridad: 'baja',
            titulo: 'Carga de datos exitosa',
            mensaje: `Se procesaron ${records} registros de ${type} desde ${filename}`,
            entidad_tipo: 'carga',
            hospital_id: hospitalId,
          })
        } else {
          addAlert({
            tipo: ALERT_TYPES.ERROR_CARGA,
            prioridad: 'alta',
            titulo: 'Error en carga de datos',
            mensaje: `Error al procesar ${filename}: ${error || 'Error desconocido'}`,
            entidad_tipo: 'carga',
            hospital_id: hospitalId,
          })
        }
      },

      // Obtener alertas filtradas por hospital
      getAlertsByHospital: (hospitalId) => {
        const { alerts } = get()
        if (!hospitalId) return alerts
        return alerts.filter(a => !a.hospital_id || a.hospital_id === hospitalId)
      },

      // Obtener conteo por tipo
      getAlertCountByType: () => {
        const { alerts } = get()
        return alerts.reduce((acc, alert) => {
          acc[alert.tipo] = (acc[alert.tipo] || 0) + 1
          return acc
        }, {})
      }
    }),
    {
      name: 'alerts-storage',
    }
  )
)

// Store de datos cargados
export const useDataStore = create((set) => ({
  hospitales: [],
  especialidades: [],
  kpis: null,
  lastUpdate: null,
  
  setHospitales: (hospitales) => set({ hospitales }),
  setEspecialidades: (especialidades) => set({ especialidades }),
  setKPIs: (kpis) => set({ kpis, lastUpdate: new Date() }),
}))
